"use client";

import { useEffect, useRef } from "react";

/**
 * Gentle scroll-linked parallax for the centered content of the full-height
 * Partner / Creator / Team sections.
 *
 * The content drifts a touch slower than its section: while you scroll *between*
 * two sections their content visually moves toward each other (the long empty
 * gap feels shorter), but the offset is exactly 0 when the section is centered
 * in the viewport — i.e. at the snap rest point each section stands alone, just
 * like before. Disabled for reduced-motion and below lg (no snap / full-height
 * there). Uses transform only, batched into a rAF, so it stays cheap.
 */
export default function ScrollParallax({
  factor = 0.15,
  className,
  children,
}: {
  factor?: number;
  className?: string;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const mq = window.matchMedia("(min-width: 1024px)");
    let raf = 0;
    let ticking = false;

    const update = () => {
      ticking = false;
      const rect = el.getBoundingClientRect();
      // distance of the content's centre from the viewport centre (0 = centred)
      const delta = rect.top + rect.height / 2 - window.innerHeight / 2;
      el.style.transform = `translate3d(0, ${(-delta * factor).toFixed(2)}px, 0)`;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      raf = requestAnimationFrame(update);
    };

    const enable = () => {
      if (mq.matches) {
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
      } else {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
        el.style.transform = "";
      }
    };

    enable();
    mq.addEventListener("change", enable);

    return () => {
      mq.removeEventListener("change", enable);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
      el.style.transform = "";
    };
  }, [factor]);

  return (
    <div ref={ref} className={className} style={{ willChange: "transform" }}>
      {children}
    </div>
  );
}
